import { useState } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { SERVICES } from "@/data/services";

const EMPTY = { first: "", last: "", email: "", phone: "", service: "", message: "", company: "" };

export const QuoteForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", {
        name: `${form.first} ${form.last}`.trim(),
        phone: form.phone,
        email: form.email,
        vehicle: "",
        service: form.service,
        notes: form.message,
        company: form.company,
      });
      setSent(true);
      toast.success("Message sent! We'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="grid grid-cols-1 lg:grid-cols-2">
      <div className="relative min-h-[320px] lg:min-h-0">
        <img src="/gallery/work-1.jpg" alt="Black Ford F-250 with showroom shine after Brad's Mobile Detailing" className="absolute inset-0 w-full h-full object-cover" />
      </div>

      <div className="px-6 sm:px-12 py-16 md:py-24 bg-[#0A0A0C]">
        <p className="font-display uppercase text-xs font-medium tracking-[0.3em] text-slate-400">Contact Us</p>
        <h2 className="mt-4 font-display uppercase font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-wide text-white leading-tight">
          Have questions? <br /> Get in touch!
        </h2>

        {sent ? (
          <div data-testid="quote-success" className="mt-10 border border-border p-10 text-center">
            <span className="mx-auto w-14 h-14 rounded-full bg-[#10B981]/15 border border-[#10B981]/40 flex items-center justify-center">
              <Send className="w-6 h-6 text-[#10B981]" />
            </span>
            <h3 className="mt-5 font-display uppercase text-lg tracking-wide">Message received</h3>
            <p className="mt-2 text-sm text-slate-400">
              Thanks {form.first || "friend"} — we'll reach out shortly.
            </p>
            <button
              data-testid="quote-send-another"
              onClick={() => { setForm(EMPTY); setSent(false); }}
              className="mt-6 text-sm text-[#FF4311] hover:underline"
            >
              Send another message
            </button>
          </div>
        ) : (
          <form data-testid="quote-form" onSubmit={submit} className="mt-9 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
            <input data-testid="quote-name-input" required value={form.first} onChange={set("first")} placeholder="Name" className="jpd-input" />
            <input data-testid="quote-lastname-input" required value={form.last} onChange={set("last")} placeholder="Last Name" className="jpd-input" />
            <input data-testid="quote-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="Email" className="jpd-input" />
            <input data-testid="quote-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="Phone" className="jpd-input" />
            <select data-testid="quote-service-select" value={form.service} onChange={set("service")} className="jpd-input sm:col-span-2 bg-[#0A0A0C]">
              <option value="">Service you're interested in…</option>
              {SERVICES.map((s) => (
                <option key={s.id} value={s.name}>{s.name}</option>
              ))}
              <option value="Polishing">Polishing</option>
              <option value="Boat / RV / Motorcycle">Boat / RV / Motorcycle</option>
              <option value="Something Else">Something Else</option>
            </select>
            <textarea data-testid="quote-message-input" required value={form.message} onChange={set("message")} placeholder="Message" rows={4} className="jpd-input sm:col-span-2 resize-none" />
            <input type="text" name="company" value={form.company} onChange={set("company")} tabIndex={-1} autoComplete="off" className="jpd-honeypot" aria-hidden="true" />
            <div className="sm:col-span-2">
              <button
                data-testid="quote-submit-button"
                disabled={loading}
                className="inline-flex items-center gap-2.5 bg-[#EA0A0B] hover:bg-[#C20A0B] disabled:opacity-60 text-white font-display uppercase tracking-[0.1em] text-sm px-10 py-4 transition-colors"
              >
                <Send className="w-4 h-4" />
                {loading ? "Sending…" : "Get in Touch"}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};
